/**
 * Pure derivations over the two-part compliance document for the record
 * page. The page renders ONE unified matrix — one row per item, columns
 * grouped Transport / Service / Temps — so the load-bearing logic here is
 * `buildUnifiedItemRows`: merging the transport tree, the service tree,
 * and the temperature readings into that row model without fabricating or
 * double-counting federal-reporting numbers.
 *
 * The wire is FLAT since the 2026-08-06 product direction — one node per
 * food item, `children` `[]` — with ONE exception since PROD-1441 (Sheila,
 * 2026-10-08): a SEALED KIT, a deliverable packing two or more components,
 * arrives as a `DELIVERABLE` node flagged `isKit` whose `children` are its
 * count-less component sub-rows. The kit is one row carrying every count
 * and reading; the sub-rows carry recipe, serving size and crediting and
 * print "in kit". The tree-shaped rules below also keep older payloads
 * working.
 *
 * Merge rules that are easy to get wrong (each pinned by unit tests):
 * - Identity keys are KIND-aware on BOTH walks: COMPONENT rows key by
 *   (bundleDeliverableId, componentId) — a promoted row still carries its
 *   deliverable's id, which its promoted siblings share, so keying bare
 *   `del:` would merge distinct food items and double-count inherited
 *   service buckets, while keying bare `cmp:` would merge the same
 *   component produced in TWO different deliverables (USDA: one row per
 *   item AS PRODUCED — the paper document keeps them separate, so must the
 *   screen); deliverable-grained rows key by bundleDeliverableId;
 *   service-only rows fall back to menuItemId, then name. Nested
 *   components are never rows.
 * - A tree-wire transport deliverable's `counts.ordered` is a prepared-echo
 *   (plannedQuantity), never the school order — Ordered comes from the
 *   bundle parent (distributed UNCHANGED to each deliverable: 100 meals ⇒
 *   100 of each package) or from the service line's PRRQ sum. On the flat
 *   wire the per-line value IS the presented figure and sums across
 *   fan-out lines like prepared; so do true à-la-carte components, whose
 *   lines carry shares of the day's order (PROD-1610).
 * - Homeroom fan-out (one node per room): SUM prepared/delivered; never
 *   sum a bundle-distributed ordered (every duplicate carries the same
 *   campus total). A top-level à-la-carte line's ordered is its SHARE of
 *   the day's order (PROD-1610), so those sum.
 * - Packaging/utensil lines (isPackaging) are excluded — they share the
 *   deliverable's identity with package-multiplied quantities.
 * - Per-count fold is a coalesce (deliverable value, else its lone food
 *   child's) — the receive flow writes RECEIVED at arbitrary grain.
 * - Tier sub-rows only when >1 real tier; single-tier portions duplicate
 *   their row and are suppressed here (the server emits them whenever the
 *   bucket has a gradeRangeId).
 * - Temps attach by id, then by name; unmatched readings are surfaced
 *   separately and still counted, so an out-of-range reading can't vanish.
 * - The record wins: a count the school ENTERED on its own record
 *   (`counts.entered`, per-tier `portions[].ordered/prepared/delivered`)
 *   beats the shipment-derived default at row and tier level.
 * - Paper AND screen flatten to one row per item x band (`printBandRows`)
 *   and group those rows into GRADE-GROUP BLOCKS (`printBlocks`): one block
 *   per band, headed `<menu span> meal pattern · grades served <band>`, its
 *   rows in three sections (main choices / shared sides and milk /
 *   condiments) with a reimbursable-meals-claimed total over the mains
 *   (PROD-1441, Sheila 2026-10-08). `ALL_GRADES_LABEL` names a block whose
 *   rows resolve no band.
 * - A MENU-derived span (`gradeSpanSource: 'MENU'`: a whole-campus line
 *   naming the band it was sold to) is legend text only and never mints a
 *   tier, on screen or on paper. Only a shipped or served band does.
 * - A tier's Ordered falls back to the shipped per-band figure when the
 *   school entered none, like prepared / delivered do.
 * - Paper never drops a figure: transport counts the tiers do not carry
 *   print on a joined-band row, and service counts outside every band (an
 *   unbanded bucket) print on an `All grades` remainder row.
 *
 * No React, no I/O — tests in
 * `client/test/components/production-record-insights.test.ts`.
 */

import type {
  DocumentLineNode,
  DocumentSignatureBlock,
  DocumentTempReading,
  ProductionRecordDocument,
  TempBand,
} from '@/api/production-records'
import { stripParentheticalTail } from '@/lib/production-records/display-name'
import {
  claimedMeals,
  mainsServedTogether,
  type MealClaimRow,
  SERVED_TOGETHER_CAPTION,
  SERVED_TOGETHER_TITLE,
} from '@/lib/production-records/meal-claim'
import { compareByKindThenName, rowKindRank, UNKNOWN_KIND_RANK } from '@/lib/production-records/row-order'

import { formatServiceDate } from './helpers'

/** SUBMITTED and AMENDED are the two signed states; everything else is unsigned. */
export function isSignedStatus(status: string): boolean {
  return status === 'SUBMITTED' || status === 'AMENDED'
}

// ---------------------------------------------------------------------------
// Signatures
// ---------------------------------------------------------------------------

/** `none` = nothing exists to sign yet (e.g. no shipments on record). */
export type SignatureRollupState = 'signed' | 'partial' | 'missing' | 'none'

export interface SignatureRollup {
  state: SignatureRollupState
  /** First signer name (per record it's usually one person throughout). */
  name: string | null
  signedAt: string | null
  signedCount: number
  total: number
}

/**
 * Aggregate one signature role across its blocks (a record with two
 * deliveries carries two kitchen + two delivery signatures — a single
 * "signed" chip must not lie about the unsigned second delivery).
 */
export function rollupSignatures(blocks: readonly DocumentSignatureBlock[]): SignatureRollup {
  const signed = blocks.filter((b) => b.name !== null && b.name.trim() !== '')
  const first = signed[0] ?? null
  const state: SignatureRollupState =
    blocks.length === 0
      ? 'none'
      : signed.length === blocks.length
        ? 'signed'
        : signed.length === 0
          ? 'missing'
          : 'partial'
  return {
    state,
    name: first?.name ?? null,
    signedAt: first?.signedAt ?? null,
    signedCount: signed.length,
    total: blocks.length,
  }
}

export function kitchenSignatureRollup(doc: ProductionRecordDocument): SignatureRollup {
  return rollupSignatures(doc.transport.groups.map((g) => g.kitchenSignature))
}

export function deliverySignatureRollup(doc: ProductionRecordDocument): SignatureRollup {
  return rollupSignatures(doc.transport.groups.map((g) => g.deliverySignature))
}

export function serviceSignatureRollup(doc: ProductionRecordDocument): SignatureRollup {
  return rollupSignatures([doc.service.signature])
}

// ---------------------------------------------------------------------------
// Unified item matrix
// ---------------------------------------------------------------------------

export type MatrixCheckpoint = 'FACILITY' | 'DELIVERY' | 'SERVICE'

const CHECKPOINT_ORDER: readonly MatrixCheckpoint[] = ['FACILITY', 'DELIVERY', 'SERVICE']

export interface MatrixTempReading {
  value: string
  unit: string
  status: DocumentTempReading['status']
  band: TempBand | null
  loggedAt: string | null
}

export interface MatrixTempCell {
  /** Distinct readings, oldest first (re-checks render "47.5 → 39.0"). */
  readings: MatrixTempReading[]
  outOfRange: boolean
}

export interface MatrixComponentLine {
  componentId: string | null
  name: string
  crediting: string | null
  creditingMissing: boolean
  /** PROD-1441: the sub-row's own serving size (recipe serving, else the line's label). */
  servingSize: string | null
  /** PROD-1441: the sub-row's recipe code (`RCP-000206`). */
  recipeCode: string | null
  /** PROD-1441: the sub-row's kind eyebrow and raw kind (`CONDIMENT` reads "not creditable"). */
  kindLabel: string | null
  componentKind: string | null
  /** PROD-1441: the sub-row's own crediting source for the paper's column. */
  creditingSource: string | null
  /** Per-component transport counts — rendered when siblings diverge (legacy tree wires only; a kit's sub-rows carry none). */
  prepared: number | null
  delivered: number | null
}

/** PROD-1441: which section of a grade-group block a row prints in. */
export type MatrixSection = 'MAIN' | 'SIDE' | 'CONDIMENT'

export interface MatrixTierRow {
  label: string
  /**
   * The grade-range NAME the band was filed under ("Preschool"), when any
   * node named one. The print drops a `servingLabel` that merely repeats it
   * (a shipment line with no serving label echoes its range name there).
   */
  gradeRangeName: string | null
  servingLabel: string | null
  /** School-entered order for this band (the PRRQ bucket); null when none was entered. */
  ordered: number | null
  prepared: number | null
  delivered: number | null
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
}

export interface MatrixItemRow {
  key: string
  name: string
  kindLabel: string | null
  /**
   * Print-only: every USDA recipe/product identifier the row stands for,
   * distinct and in first-seen order. A row merged from two productions of
   * the same component (`mergeDeliverableComponentRows`) carries BOTH — an
   * auditor looks these up, so silently keeping the first would hide one.
   * Empty when nothing on the row names a recipe.
   */
  recipeIds: string[]
  crediting: string | null
  creditingMissing: boolean
  /** "1 Bowlpak" / "1 cup (8 oz spoodle)" — recipe yield else portion label. */
  servingSize: string | null
  gradeSpan: string | null
  /** Reduced-order rate in bps, when configured below 100%. */
  rateBps: number | null
  isBulk: boolean
  /** Per-line OVS/SS; render only when the header style is null. */
  serviceStyle: 'OVS' | 'SS' | null
  /** 1-based transport groups this item shipped in (delivery tag when >1 group). */
  deliveryIndexes: number[]
  ordered: number | null
  prepared: number | null
  delivered: number | null
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
  temps: Partial<Record<MatrixCheckpoint, MatrixTempCell>>
  /** Component sub-lines — only when the package holds 2+ components. */
  components: MatrixComponentLine[]
  /** Per-age-group sub-rows — only when >1 real tier. */
  tiers: MatrixTierRow[]
  /**
   * PROD-1441: a SEALED KIT — the row carries the counts, `components` are
   * its count-less sub-rows ("in kit"). True whenever the wire flagged the
   * node or a package shows two or more component sub-lines.
   */
  isKit: boolean
  /** PROD-1441: the recipe codes the row itself stands for (`RCP-000206`), first-seen order, never a cuid. */
  recipeCodes: string[]
  /** PROD-1441: the row's own raw component kind (standalone component rows); null on a kit. */
  componentKind: string | null
  /** PROD-1441: the grade-group block section the row prints in. */
  section: MatrixSection
  /**
   * The paper's kind order inside the section (`rowKindRank`): main, grain,
   * vegetable, fruit, milk, side, condiment, unknown last — the edit screen's
   * order. Optional: a row built without it sorts last, by name.
   */
  sortRank?: number
  /** PROD-1441: the paper's Crediting source column (`Std. recipe`, `CN label`, …); null when nothing on file says. */
  creditingSource: string | null
  /** PROD-1441: the grade span the item's MENU was planned for, for the block header. */
  menuGradeSpan: string | null
  /**
   * The meals the row belongs to (`bundle:<id>`), for the claim: a meal's two
   * mains are ONE meal (`claimedMeals`, Amana 2026-11-03). Optional: a row
   * built without it is a meal of its own.
   */
  mealKeys?: string[]
  /**
   * A MILK row whose `delivered` is the school's own on-hand count, entered on
   * the record (Amana, 2026-11-03): what the meal started with, leftovers from
   * earlier meals included, so Prepared − Served = Leftover reads true on
   * paper. The truck's own count stays on the delivery receipt. Optional: a
   * row built without it prints the delivery. Data only since 2026-11-04: the
   * surfaces print every row's on-hand count as Prepared with no milk label.
   */
  receivedIsStartedWith?: boolean
  /**
   * A milk row's field-trip line (2026-11-04): the cartons sent out with
   * field-trip meals (lowers Leftover, never Served), and the milk the Field
   * Trip menu ordered (kept out of Planned). Null when neither is on the
   * record. Optional: a row built without them has no field-trip line.
   */
  fieldTrip?: { sent: number; planned: number } | null
  /** The row is a meal sold only on the Field Trip menu (`relocateFieldTripMilk` anchors the trip's milk here). */
  fieldTripMeal?: boolean
  /**
   * The milk sent with this field-trip meal, one line per milk (2026-11-04,
   * Sheila: "the milk should sort with the field trip menu item itself").
   * Set on the field-trip meal row only; the milk rows' own `fieldTrip` is
   * cleared when it moves here.
   */
  fieldTripMilk?: FieldTripMilkLine[]
  /** A line of the row credits MILK: the blank sheet prints its field-trip write-in line. Optional: absent reads as food. */
  isMilk?: boolean
}

/** One milk's field-trip line under a field-trip meal: the milk, the cartons sent, the trip's planned milk. */
export interface FieldTripMilkLine {
  /** The milk row's key, so a renderer can tie the line back to it. */
  milkKey: string
  name: string
  sent: number
  planned: number
}

/** The field-trip line's printed name. */
export const FIELD_TRIP_PRINT_LABEL = 'Sent on field trip'

/** The field-trip line's legend: the Field Trip menu's planned milk, when it ordered any. */
export function fieldTripPrintLegend(fieldTrip: { planned: number }): string | null {
  return fieldTrip.planned > 0 ? `${fieldTrip.planned} planned on field-trip meals` : null
}

/** One printed field-trip line under a row: its label, the cartons sent, the trip's planned milk. */
export interface FieldTripPrintLine {
  key: string
  /** `Sent on field trip` under a milk row; `<milk> sent on field trip` under a field-trip meal. */
  label: string
  sent: number
  planned: number
}

/**
 * The field-trip lines a printed row carries, in order: one per milk under a
 * field-trip meal (`fieldTripMilk`), else the milk row's own line when no
 * meal took it. Only an item's first band row prints them. Pure.
 */
export function fieldTripPrintLines(printRow: Pick<PrintBandRow, 'row' | 'firstOfItem'>): FieldTripPrintLine[] {
  if (!printRow.firstOfItem) {
    return []
  }
  const { row } = printRow
  if (row.fieldTripMilk && row.fieldTripMilk.length > 0) {
    return row.fieldTripMilk.map((line) => ({
      key: line.milkKey,
      label: `${line.name} ${FIELD_TRIP_PRINT_LABEL.toLowerCase()}`,
      sent: line.sent,
      planned: line.planned,
    }))
  }
  return row.fieldTrip ? [{ key: row.key, label: FIELD_TRIP_PRINT_LABEL, ...row.fieldTrip }] : []
}

export interface MatrixDelivery {
  /** 1-based, in transport-group order. */
  index: number
  deliveryMode: string
  handoutMode: string
  deliveryDate: string
  dayBefore: boolean
  receivedAt: string | null
}

export interface MatrixUnmatchedTemp extends MatrixTempReading {
  checkpoint: MatrixCheckpoint
  lineName: string | null
}

export interface TempHealth {
  total: number
  inRange: number
  outOfRange: number
  notRecorded: number
}

export interface UnifiedMatrix {
  rows: MatrixItemRow[]
  /** Checkpoints that have any reading — drives which temp columns render. */
  checkpoints: MatrixCheckpoint[]
  /** Readings matching no row (whole-shipment/-record, legacy) — shown + counted. */
  unmatched: MatrixUnmatchedTemp[]
  /** Over the SAME deduped readings the matrix renders (not the raw fan-out). */
  tempHealth: TempHealth
  deliveries: MatrixDelivery[]
  /** Non-null when every delivery shares one (mode, handout) pair — header chip. */
  sharedModePair: { deliveryMode: string; handoutMode: string } | null
}

// Internal accumulator ------------------------------------------------------

interface TierAcc {
  label: string
  gradeRangeName: string | null
  servingLabel: string | null
  /** Shipped per-band ordered (flat-wire single-band lines); an entered figure wins at finalize. */
  ordered: number | null
  prepared: number | null
  delivered: number | null
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
  /**
   * PROD-1279: the school's OWN per-band figures (`DocumentPortionRow.ordered`
   * / `prepared` / `delivered` carry entered values only). When present they
   * win over the shipment-derived tier counts at finalize.
   */
  enteredOrdered: number | null
  enteredPrepared: number | null
  enteredDelivered: number | null
}

interface RowAcc {
  key: string
  name: string
  kindLabel: string | null
  recipeIds: string[]
  crediting: string | null
  creditingMissing: boolean
  servingSize: string | null
  gradeSpans: Set<string>
  rateBps: number | null
  isBulk: boolean
  serviceStyle: 'OVS' | 'SS' | null
  deliveryIndexes: Set<number>
  ordered: number | null
  prepared: number | null
  delivered: number | null
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
  /**
   * PROD-1279: sums of the service lines' `counts.ordered` / `prepared` /
   * `delivered` where `counts.entered` marks them as SCHOOL-ENTERED. At
   * finalize an entered figure wins over the transport walk's default, so
   * the record shows what the school wrote down; null means nothing was
   * entered and the shipment-derived number stands.
   */
  enteredOrdered: number | null
  enteredPrepared: number | null
  enteredDelivered: number | null
  temps: Map<MatrixCheckpoint, Map<string, MatrixTempReading>>
  components: Map<string, MatrixComponentLine>
  tiers: Map<string, TierAcc>
  isKit: boolean
  recipeCodes: string[]
  componentKind: string | null
  /** The raw kinds of the row's component sub-lines, for the section rule. */
  componentKinds: Set<string>
  creditingSource: string | null
  menuGradeSpan: string | null
  /** `bundle:<id>` of every bundle the row's lines belong to (`MatrixItemRow.mealKeys`). */
  mealKeys: Set<string>
  /** A line of the row credits MILK (`MatrixItemRow.receivedIsStartedWith`). */
  isMilk: boolean
  /** A service line of the row is a meal sold only on the Field Trip menu. */
  fieldTripMeal: boolean
  /** Σ `counts.fieldTrip` over the row's service lines. */
  fieldTripSent: number
  /** Σ `counts.ordered` over the row's Field Trip menu lines (kept out of `ordered`). */
  fieldTripPlanned: number
}

const UNNAMED = '(Unnamed line)'

/**
 * Identity for a promoted component row: the same component shipped in TWO
 * different deliverables (two bundles each packing Baked Beans) accumulates
 * as two productions. Keying such rows by bare `cmp:` at THIS stage merged
 * them before the walks finished and double-counted inherited service
 * buckets. Rows from the SAME deliverable (homeroom fan-out, transport +
 * service walks of one physical item) share this key and still merge. A
 * later pass (`mergeDeliverableComponentRows`, owner direction 2026-08-24)
 * collapses same-component productions across bundles into one presented
 * row when their facts agree.
 */
function componentRowKey(node: Pick<DocumentLineNode, 'bundleDeliverableId' | 'componentId'>): string {
  return node.bundleDeliverableId
    ? `del:${node.bundleDeliverableId}:cmp:${node.componentId}`
    : `cmp:${node.componentId}`
}

/**
 * The component identity a row key carries, if any. Keys are minted only in
 * this module: `del:<id>:cmp:<cid>` (promoted component), `cmp:<cid>`
 * (à-la-carte / synthetic single-pick), `del:<id>`, `mi:<id>`, `name:<name>`.
 */
function componentIdOfKey(key: string): string | null {
  if (key.startsWith('name:') || key.startsWith('mi:')) {
    return null
  }
  const marker = key.startsWith('cmp:') ? 4 : key.indexOf(':cmp:') >= 0 ? key.indexOf(':cmp:') + 5 : -1
  return marker >= 0 ? key.slice(marker) : null
}

/**
 * True when one bundle contributes two of these rows: two deliverables of the
 * SAME meal packing the same component are two packs per meal (Amana's
 * breakfast, 2026-11-03: two Orange Wedges deliverables), each produced and
 * served as its own item — never one row of their sum.
 */
function packsOneMealTwice(rows: readonly RowAcc[]): boolean {
  const seen = new Set<string>()
  for (const row of rows) {
    for (const mealKey of row.mealKeys) {
      if (seen.has(mealKey)) {
        return true
      }
    }
    for (const mealKey of row.mealKeys) {
      seen.add(mealKey)
    }
  }
  return false
}

/**
 * Collapse rows that present the SAME physical component produced in
 * different parent bundles into one deliverable row: counts sum, temps and
 * tiers union, and the bundle-suffixed names fall back to the shared base
 * name. Rows whose production facts diverge (crediting, serving size, bulk
 * flag) never merge — merging incompatible facts would make the row lie.
 * Mutates `rows` in place, preserving first-appearance order.
 */
function mergeDeliverableComponentRows(rows: Map<string, RowAcc>): void {
  const byComponent = new Map<string, RowAcc[]>()
  for (const acc of rows.values()) {
    const componentId = componentIdOfKey(acc.key)
    if (!componentId) {
      continue
    }
    // Facts must agree for two productions to present as one deliverable.
    const factsKey = [componentId, acc.crediting ?? '', acc.servingSize ?? '', acc.isBulk ? 'bulk' : 'each'].join(' ')
    const group = byComponent.get(factsKey)
    if (group) {
      group.push(acc)
    } else {
      byComponent.set(factsKey, [acc])
    }
  }
  for (const group of byComponent.values()) {
    if (group.length < 2 || packsOneMealTwice(group)) {
      continue
    }
    const [head, ...rest] = group as [RowAcc, ...RowAcc[]]
    // Bundle-disambiguation suffixes drop once the segments merge — but only
    // when the names actually differed AND agree on the same base; genuine
    // parenthetical size labels on an already-shared name stay.
    const names = group.map((r) => r.name)
    if (new Set(names).size > 1) {
      const stripped = new Set(names.map(stripParentheticalTail))
      if (stripped.size === 1) {
        head.name = [...stripped][0]!
      }
    }
    for (const other of rest) {
      head.ordered = addNullable(head.ordered, other.ordered)
      head.prepared = addNullable(head.prepared, other.prepared)
      head.delivered = addNullable(head.delivered, other.delivered)
      head.served = addNullable(head.served, other.served)
      head.reimbursable = addNullable(head.reimbursable, other.reimbursable)
      head.nonReimbursable = addNullable(head.nonReimbursable, other.nonReimbursable)
      head.leftover = addNullable(head.leftover, other.leftover)
      head.enteredOrdered = addNullable(head.enteredOrdered, other.enteredOrdered)
      head.enteredPrepared = addNullable(head.enteredPrepared, other.enteredPrepared)
      head.enteredDelivered = addNullable(head.enteredDelivered, other.enteredDelivered)
      head.kindLabel ??= other.kindLabel
      for (const recipeId of other.recipeIds) {
        addRecipeId(head.recipeIds, recipeId)
      }
      for (const recipeCode of other.recipeCodes) {
        addRecipeId(head.recipeCodes, recipeCode)
      }
      head.componentKind ??= other.componentKind
      head.creditingSource ??= other.creditingSource
      head.menuGradeSpan ??= other.menuGradeSpan
      head.creditingMissing ||= other.creditingMissing
      head.rateBps = head.rateBps === other.rateBps ? head.rateBps : null
      head.serviceStyle = head.serviceStyle === other.serviceStyle ? head.serviceStyle : null
      for (const span of other.gradeSpans) {
        head.gradeSpans.add(span)
      }
      for (const index of other.deliveryIndexes) {
        head.deliveryIndexes.add(index)
      }
      head.isMilk ||= other.isMilk
      for (const mealKey of other.mealKeys) {
        head.mealKeys.add(mealKey)
      }
      for (const [checkpoint, cell] of other.temps) {
        let target = head.temps.get(checkpoint)
        if (!target) {
          target = new Map()
          head.temps.set(checkpoint, target)
        }
        for (const [dedupKey, reading] of cell) {
          const existing = target.get(dedupKey)
          if (
            !existing ||
            (reading.loggedAt !== null && (existing.loggedAt === null || reading.loggedAt < existing.loggedAt))
          ) {
            target.set(dedupKey, reading)
          }
        }
      }
      for (const [id, component] of other.components) {
        const existing = head.components.get(id)
        if (existing) {
          existing.prepared = addNullable(existing.prepared, component.prepared)
          existing.delivered = addNullable(existing.delivered, component.delivered)
        } else {
          head.components.set(id, component)
        }
      }
      for (const [label, tier] of other.tiers) {
        const target = head.tiers.get(label)
        if (target) {
          target.gradeRangeName ??= tier.gradeRangeName
          target.servingLabel ??= tier.servingLabel
          target.ordered = addNullable(target.ordered, tier.ordered)
          target.prepared = addNullable(target.prepared, tier.prepared)
          target.delivered = addNullable(target.delivered, tier.delivered)
          target.served = addNullable(target.served, tier.served)
          target.reimbursable = addNullable(target.reimbursable, tier.reimbursable)
          target.nonReimbursable = addNullable(target.nonReimbursable, tier.nonReimbursable)
          target.leftover = addNullable(target.leftover, tier.leftover)
          target.enteredOrdered = addNullable(target.enteredOrdered, tier.enteredOrdered)
          target.enteredPrepared = addNullable(target.enteredPrepared, tier.enteredPrepared)
          target.enteredDelivered = addNullable(target.enteredDelivered, tier.enteredDelivered)
        } else {
          head.tiers.set(label, tier)
        }
      }
      rows.delete(other.key)
    }
  }
}

function addNullable(a: number | null, b: number | null): number | null {
  if (a === null && b === null) {
    return null
  }
  return (a ?? 0) + (b ?? 0)
}

function isFoodComponent(node: DocumentLineNode): boolean {
  return node.kind === 'COMPONENT' && !node.isPackaging
}

/** One kit sub-line from a component child node (PROD-1441). */
function componentLineOf(child: DocumentLineNode, counts: boolean): MatrixComponentLine {
  return {
    componentId: child.componentId,
    name: child.name,
    crediting: creditingSummary(child),
    creditingMissing: child.creditingMissing,
    servingSize: child.recipeServingSize ?? child.servingLabel,
    recipeCode: child.recipeCode ?? null,
    kindLabel: child.kindLabel,
    componentKind: child.componentKind ?? null,
    creditingSource: child.creditingSource ?? null,
    prepared: counts ? child.counts.prepared : null,
    delivered: counts ? child.counts.delivered : null,
  }
}

/** Record a package's component sub-lines on the row and flag it a kit. */
function foldComponents(acc: RowAcc, children: readonly DocumentLineNode[], counts: boolean): void {
  acc.isKit = true
  for (const child of children) {
    const id = child.componentId ?? child.name
    const kind = child.componentKind ?? (child.kindLabel ? kindFromLabel(child.kindLabel) : null)
    if (kind) {
      acc.componentKinds.add(kind)
    }
    const existing = acc.components.get(id)
    if (existing) {
      existing.prepared = addNullable(existing.prepared, counts ? child.counts.prepared : null)
      existing.delivered = addNullable(existing.delivered, counts ? child.counts.delivered : null)
      existing.servingSize ??= child.recipeServingSize ?? child.servingLabel
      existing.recipeCode ??= child.recipeCode ?? null
      existing.kindLabel ??= child.kindLabel
      existing.componentKind ??= child.componentKind ?? null
      existing.creditingSource ??= child.creditingSource ?? null
    } else {
      acc.components.set(id, componentLineOf(child, counts))
    }
  }
}

/** The raw kind a legacy wire's eyebrow label stands for (no `componentKind` on the node). */
function kindFromLabel(kindLabel: string): string | null {
  switch (kindLabel) {
    case 'MAIN':
      return 'MAIN_ENTREE'
    case 'CONDIMENT':
      return 'CONDIMENT'
    case 'MILK/BEVERAGE':
      return 'BEVERAGE'
    default:
      return null
  }
}

/**
 * Which block section a row prints in (PROD-1441): a kit holding a main
 * entrée, or a standalone main, is a MAIN CHOICE; a condiment (or a kit of
 * nothing but condiments) is a CONDIMENT; everything else is a SHARED SIDE
 * or milk. Legacy wires without raw kinds fall back to the eyebrow label.
 */
function rowKinds(acc: RowAcc): string[] {
  const own = acc.componentKind ?? (acc.kindLabel ? kindFromLabel(acc.kindLabel) : null)
  return acc.components.size >= 2 ? [...acc.componentKinds] : own ? [own] : []
}

function sectionOf(acc: RowAcc): MatrixSection {
  const kinds = rowKinds(acc)
  if (kinds.includes('MAIN_ENTREE')) {
    return 'MAIN'
  }
  if (kinds.length > 0 && kinds.every((kind) => kind === 'CONDIMENT')) {
    return 'CONDIMENT'
  }
  return 'SIDE'
}

/** Append a recipe id once, keeping first-seen order. Nulls and repeats are no-ops. */
function addRecipeId(list: string[], recipeId: string | null): void {
  if (recipeId && !list.includes(recipeId)) {
    list.push(recipeId)
  }
}

function tierLabelOf(node: Pick<DocumentLineNode, 'gradeSpan' | 'gradeRangeName'>): string | null {
  return node.gradeSpan ?? node.gradeRangeName
}

/**
 * The band a node was actually SHIPPED to, for minting a tier. A
 * MENU-derived span (`gradeSpanSource: 'MENU'`: a whole-campus line naming
 * the band it was sold to) is legend text and never a tier, so only its
 * range name (null for a menu span) could still name one. A wire without
 * the field is the pre-PROD-1279 shape, where every span was a real band.
 */
function shippedBandOf(
  node: Pick<DocumentLineNode, 'gradeSpan' | 'gradeRangeName' | 'gradeSpanSource'>,
): string | null {
  return node.gradeSpanSource === 'MENU' ? node.gradeRangeName : tierLabelOf(node)
}

/**
 * The paper's name-collapse rule now lives in `lib/production-records/display-name.ts`,
 * shared with the unified input's row model (PROD-1441); re-exported so the
 * print tests and callers keep their import.
 */
export { stripParentheticalTail }

/** "M/MA 2 oz eq · Grain 1 oz eq" — null when the node carries no crediting. */
export function creditingSummary(node: Pick<DocumentLineNode, 'crediting'>): string | null {
  if (!node.crediting || node.crediting.length === 0) {
    return null
  }
  return node.crediting.map((c) => `${c.label} ${c.display}`).join(' · ')
}

/** "MILK/BEVERAGE" to "Milk/Beverage" — the kind eyebrow, Title Cased per segment. */
export function kindTitle(kindLabel: string): string {
  return kindLabel
    .split('/')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join('/')
}

/** One labelled legend field: `label` null for a bare token (the kind eyebrow, the delivery tag). */
export interface LegendField {
  label: string | null
  text: string
}

/** The kit row's legend: how many recipes ride inside, and that the counts are per kit (PROD-1441). */
export function kitLegendText(componentCount: number): string {
  return `${componentCount} recipes packed together · counts are per kit`
}

/** The condiment legend the paper prints in place of a crediting gap marker (PROD-1441). */
export const CONDIMENT_LEGEND = 'Condiment · not creditable'
/** A non-condiment with no approved crediting on file (replaces the old "—*" footnote). */
export const CREDITING_NOT_ON_FILE = 'not on file'

/**
 * The labelled legend under an item name — the SAME fields on screen and on
 * paper (PROD-1279: one derivation, two surfaces). Sheila (2026-10-08):
 * the serving size and the crediting are DELINEATED from the product type
 * instead of one dot-separated string, the age group lives in the block
 * header (never here), and the reduced-order rate is gone:
 *
 *   - the kind eyebrow (`Main`), bare;
 *   - `Serving 1 Salad`;
 *   - `Credits M/MA 2 oz eq · Grain 1 ½ oz eq`, or `Credits not on file`
 *     for a non-condiment with no approved label; a condiment reads
 *     `Condiment · not creditable` instead;
 *   - `Recipe RCP-000206` (every code the row stands for) when
 *     `includeRecipe`;
 *   - `Del 2` on a multi-truck day, bare.
 *
 * A sealed kit reads `N recipes packed together · counts are per kit`: its
 * sub-rows carry the serving, credits and recipe of each recipe inside.
 *
 * No milk "started with" note (Amana audit week, 2026-11-04): the column a
 * milk row's on-hand count prints in is PREPARED on every surface now, and
 * Prepared means what the meal had on hand on every row, food and milk alike,
 * so the row needs no label of its own.
 */
export function legendFields(
  row: MatrixItemRow,
  opts: { multiDelivery: boolean; includeRecipe?: boolean; servingSize?: string | null },
): LegendField[] {
  const fields: LegendField[] = []
  const delivery =
    opts.multiDelivery && row.deliveryIndexes.length > 0
      ? { label: null, text: `Del ${row.deliveryIndexes.join('+')}` }
      : null
  if (row.isKit) {
    fields.push({ label: null, text: kitLegendText(row.components.length) })
    if (delivery) {
      fields.push(delivery)
    }
    return fields
  }
  const condiment = isCondimentRow(row)
  if (condiment) {
    fields.push({ label: null, text: CONDIMENT_LEGEND })
  } else if (row.kindLabel) {
    fields.push({ label: null, text: kindTitle(row.kindLabel) })
  }
  const servingSize = opts.servingSize === undefined ? row.servingSize : opts.servingSize
  if (servingSize) {
    fields.push({ label: 'Serving', text: servingSize })
  }
  if (!condiment) {
    if (row.crediting) {
      fields.push({ label: 'Credits', text: row.crediting })
    } else if (row.creditingMissing) {
      fields.push({ label: 'Credits', text: CREDITING_NOT_ON_FILE })
    }
  }
  if (opts.includeRecipe === true && row.recipeCodes.length > 0) {
    fields.push({ label: 'Recipe', text: row.recipeCodes.join(', ') })
  }
  if (delivery) {
    fields.push(delivery)
  }
  return fields
}

/** The labelled legend of one kit sub-row: serving, credits (or the condiment note), recipe (PROD-1441). */
export function componentLegendFields(
  component: MatrixComponentLine,
  opts: { includeRecipe?: boolean },
): LegendField[] {
  const fields: LegendField[] = []
  const condiment = component.componentKind === 'CONDIMENT' || component.kindLabel === 'CONDIMENT'
  if (condiment) {
    fields.push({ label: null, text: CONDIMENT_LEGEND })
  } else if (component.kindLabel) {
    fields.push({ label: null, text: kindTitle(component.kindLabel) })
  }
  if (component.servingSize) {
    fields.push({ label: 'Serving', text: component.servingSize })
  }
  if (!condiment) {
    if (component.crediting) {
      fields.push({ label: 'Credits', text: component.crediting })
    } else if (component.creditingMissing) {
      fields.push({ label: 'Credits', text: CREDITING_NOT_ON_FILE })
    }
  }
  if (opts.includeRecipe === true && component.recipeCode) {
    fields.push({ label: 'Recipe', text: component.recipeCode })
  }
  return fields
}

function isCondimentRow(row: Pick<MatrixItemRow, 'componentKind' | 'kindLabel' | 'isKit'>): boolean {
  return !row.isKit && (row.componentKind === 'CONDIMENT' || row.kindLabel === 'CONDIMENT')
}

/** A legend field as one text token (`Serving 1 Salad`). */
export function legendFieldText(field: LegendField): string {
  return field.label ? `${field.label} ${field.text}` : field.text
}

/**
 * The legend as plain tokens — `legendFields` joined per field — for the
 * CSV export and any surface that cannot lay fields out. One derivation.
 */
export function itemLegendMeta(
  row: MatrixItemRow,
  opts: { multiDelivery: boolean; includeRecipe?: boolean },
): string[] {
  return legendFields(row, opts).map(legendFieldText)
}

/** A tier's presented row: the school's entered per-band figures win over the shipped ones. */
function finalizeTier(tier: TierAcc): MatrixTierRow {
  return {
    label: tier.label,
    gradeRangeName: tier.gradeRangeName,
    servingLabel: tier.servingLabel,
    ordered: tier.enteredOrdered ?? tier.ordered,
    prepared: tier.enteredPrepared ?? tier.prepared,
    delivered: tier.enteredDelivered ?? tier.delivered,
    served: tier.served,
    reimbursable: tier.reimbursable,
    nonReimbursable: tier.nonReimbursable,
    leftover: tier.leftover,
  }
}

// ---------------------------------------------------------------------------
// Print rows (PROD-1279 stage-1 feedback A1): one row per item x band
// ---------------------------------------------------------------------------

/**
 * Paper-only band label for a row that resolves no grade span at all. An
 * untagged menu serves every band (`menu-grade-range-gate.ts`), so the
 * honest label is campus-wide. The digital record view never shows it and
 * the wire stays null for "no band known".
 */
export const ALL_GRADES_LABEL = 'All grades'

/** One printed matrix row: an item served to one age band, with that band's counts. */
export interface PrintBandRow {
  /**
   * `row.key` for a single-band item, `row.key::band` per tier otherwise;
   * the joined-band transport row and the `All grades` remainder row use
   * the same `row.key::band` form.
   */
  key: string
  row: MatrixItemRow
  /** The age range served, as legend TEXT (never a badge). */
  band: string
  servingLabel: string | null
  /** True on the first row of each item: component sub-lines print once. */
  firstOfItem: boolean
  ordered: number | null
  prepared: number | null
  delivered: number | null
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
}

/**
 * A tier's own serving label, unless it is only the band echoed back (a
 * shipment line with no serving label repeats its range NAME or span
 * there); then the item's serving size is the honest label.
 */
function tierServingLabel(row: MatrixItemRow, tier: MatrixTierRow): string | null {
  if (tier.servingLabel && tier.servingLabel !== tier.label && tier.servingLabel !== tier.gradeRangeName) {
    return tier.servingLabel
  }
  return row.servingSize
}

/**
 * Flatten the matrix for paper: every printed row names the age band it was
 * served to. A multi-band item becomes one row per band carrying THAT
 * band's counts (no item total row); a single-band item is one row whose
 * band is its span, or `ALL_GRADES_LABEL` when nothing resolved.
 *
 * Collapse rule (lossless paper): when the item carries a served total but
 * NO band does (`row.served !== null` and every `tier.served === null`), the
 * bands are only a transport breakdown and the service count lives on the
 * item, so ONE row prints with the bands joined as its label and the item's
 * counts. Splitting it would drop the served total from the sheet.
 *
 * Two more rows keep the paper lossless when the bands and the figures sit
 * at different grains. Transport counts (Ordered / Prepared / Received) that
 * NO band row carries, column by column, print FIRST on one row labelled
 * with every band joined (the whole-campus shipment behind a per-band
 * service, or a bundle-distributed Ordered beside shipped band counts) with
 * blank service cells. Service counts left over once the band rows are
 * summed (an unbanded roster bucket beside banded ones) print LAST on an
 * `ALL_GRADES_LABEL` row with blank transport cells. Either way every
 * figure the screen shows is on exactly one printed row.
 */
export function printBandRows(rows: readonly MatrixItemRow[]): PrintBandRow[] {
  const out: PrintBandRow[] = []
  for (const row of rows) {
    const banded = row.tiers.length >= 2
    const collapsed = banded && row.served !== null && row.tiers.every((tier) => tier.served === null)
    if (banded && !collapsed) {
      const transportRow = itemTransportRow(row)
      if (transportRow) {
        out.push(transportRow)
      }
      row.tiers.forEach((tier, i) => {
        out.push({
          key: `${row.key}::${tier.label}`,
          row,
          band: tier.label,
          servingLabel: tierServingLabel(row, tier),
          firstOfItem: transportRow === null && i === 0,
          ordered: tier.ordered,
          prepared: tier.prepared,
          delivered: tier.delivered,
          served: tier.served,
          reimbursable: tier.reimbursable,
          nonReimbursable: tier.nonReimbursable,
          leftover: tier.leftover,
        })
      })
      const remainder = unbandedRemainderRow(row)
      if (remainder) {
        out.push(remainder)
      }
      continue
    }
    out.push({
      key: row.key,
      row,
      band: collapsed ? row.tiers.map((tier) => tier.label).join(', ') : (row.gradeSpan ?? ALL_GRADES_LABEL),
      servingLabel: row.servingSize,
      firstOfItem: true,
      ordered: row.ordered,
      prepared: row.prepared,
      delivered: row.delivered,
      served: row.served,
      reimbursable: row.reimbursable,
      nonReimbursable: row.nonReimbursable,
      leftover: row.leftover,
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// Grade-group blocks (PROD-1441): band → sections → rows, with the claim total
// ---------------------------------------------------------------------------

export interface PrintBlockSection {
  key: MatrixSection
  title: string
  /** The explanatory caption (`student selects one`); null under straight serve, or when captions are off. */
  caption: string | null
  rows: PrintBandRow[]
}

export interface ClaimedTotal {
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
}

export interface PrintBlock {
  /** The band the block stands for (`K-8`, or `ALL_GRADES_LABEL`). */
  band: string
  /** The menu's planned span when every row of the block agrees on one (`K-8`); null otherwise. */
  mealPattern: string | null
  /** `K-8 meal pattern · grades served 6-8`, or whichever half resolves. */
  heading: string
  sections: PrintBlockSection[]
  /** Reimbursable meals claimed = Σ Served / Reimb / Non-reimb over the MAIN CHOICES rows; null when the block has no mains. */
  claimed: ClaimedTotal | null
}

const SECTION_ORDER: readonly MatrixSection[] = ['MAIN', 'SIDE', 'CONDIMENT']

export const SECTION_TITLE: Record<MatrixSection, string> = {
  MAIN: 'Main choices',
  SIDE: 'Shared sides & milk',
  CONDIMENT: 'Condiments',
}

/** The caption under a section title, by service style; a condiment section always says why it does not credit. */
export function sectionCaption(section: MatrixSection, serviceStyle: 'OVS' | 'SS' | null): string | null {
  switch (section) {
    case 'MAIN':
      return serviceStyle === 'OVS' ? 'student selects one' : null
    case 'SIDE':
      return serviceStyle === 'OVS' ? 'offered with any main' : null
    case 'CONDIMENT':
      return 'not creditable'
  }
}

/** The grade-group heading: the meal pattern the menu was planned for, and the grades actually served. */
export function gradeGroupHeading(band: string, mealPattern: string | null): string {
  if (mealPattern === null || mealPattern === band) {
    return band
  }
  return `${mealPattern} meal pattern · grades served ${band}`
}

/**
 * Group the printed rows into GRADE-GROUP BLOCKS (Sheila, 2026-10-08: "we
 * need to split grade groups from each other"): one block per band in
 * first-seen order, headed by the meal pattern and the grades served; inside,
 * the rows in three sections (main choices / shared sides and milk /
 * condiments, empty ones skipped) in their printed order; and the block's
 * reimbursable meals claimed, the Served / Reimb / Non-reimb summed over its
 * main choices (the mock's row 9), null when the block has no mains. Pure.
 */
/**
 * The field-trip meal the trip's milk files under: the first row sold only on
 * the Field Trip menu, a main choice first. Null when the record has none.
 */
export function fieldTripAnchor(rows: readonly MatrixItemRow[]): MatrixItemRow | null {
  const meals = rows.filter((row) => row.fieldTripMeal === true)
  return meals.find((row) => row.section === 'MAIN') ?? meals[0] ?? null
}

/**
 * Move each milk's field-trip line (`fieldTrip`) under the field-trip meal
 * (`fieldTripMilk` on `fieldTripAnchor`), in the milk rows' order, so the
 * record reads the trip as one block: the meal, then the milk that left with
 * it. Every milk on the record gets a line (the school does not know ahead
 * which milk leaves). With no field-trip meal on the record the lines stay on
 * their milk rows.
 * Pure: returns new rows.
 */
export function relocateFieldTripMilk(rows: readonly MatrixItemRow[]): MatrixItemRow[] {
  const anchor = fieldTripAnchor(rows)
  if (!anchor) {
    return [...rows]
  }
  // Every milk on the record: the school does not know ahead which milk leaves with the trip.
  const lines: FieldTripMilkLine[] = rows.flatMap((row) =>
    row.isMilk === true || row.fieldTrip
      ? [{ milkKey: row.key, name: row.name, sent: row.fieldTrip?.sent ?? 0, planned: row.fieldTrip?.planned ?? 0 }]
      : [],
  )
  if (lines.length === 0) {
    return [...rows]
  }
  return rows.map((row) => {
    if (row.key === anchor.key) {
      return { ...row, fieldTripMilk: lines }
    }
    return row.fieldTrip ? { ...row, fieldTrip: null } : row
  })
}

/**
 * A section's rows in the edit screen's order (`row-order.ts`): kind rank,
 * then name. An item's band rows move as one block, in their own order, so a
 * multi-band item never splits around another item. Pure.
 */
function sortSectionRows(rows: readonly PrintBandRow[]): PrintBandRow[] {
  const items: Array<{ sortRank: number; name: string; rows: PrintBandRow[] }> = []
  const byKey = new Map<string, PrintBandRow[]>()
  for (const printRow of rows) {
    const existing = byKey.get(printRow.row.key)
    if (existing) {
      existing.push(printRow)
      continue
    }
    const block = [printRow]
    byKey.set(printRow.row.key, block)
    items.push({ sortRank: printRow.row.sortRank ?? UNKNOWN_KIND_RANK, name: printRow.row.name, rows: block })
  }
  return [...items].sort(compareByKindThenName).flatMap((item) => item.rows)
}

export function printBlocks(
  rows: readonly PrintBandRow[],
  opts: { serviceStyle: 'OVS' | 'SS' | null; captions?: boolean },
): PrintBlock[] {
  const byBand = new Map<string, PrintBandRow[]>()
  for (const printRow of rows) {
    byBand.set(printRow.band, [...(byBand.get(printRow.band) ?? []), printRow])
  }
  return [...byBand].map(([band, bandRows]) => {
    const patterns = new Set(bandRows.map((r) => r.row.menuGradeSpan).filter((span): span is string => span !== null))
    const mealPattern = patterns.size === 1 ? [...patterns][0]! : null
    const mains = bandRows.filter((r) => r.row.section === 'MAIN')
    // Per MEAL: a meal's two mains are one meal (`claimedMeals`, Amana 2026-11-03).
    const claimRows: MealClaimRow[] = mains.map((r) => ({
      mealKeys: r.row.mealKeys ?? [],
      served: r.served,
      reimbursable: r.reimbursable,
      nonReimbursable: r.nonReimbursable,
    }))
    const together = mainsServedTogether(claimRows)
    const sections = SECTION_ORDER.flatMap((section) => {
      const sectionRows = sortSectionRows(bandRows.filter((r) => r.row.section === section))
      if (sectionRows.length === 0) {
        return []
      }
      const servedTogether = section === 'MAIN' && together
      return [
        {
          key: section,
          title: servedTogether ? SERVED_TOGETHER_TITLE : SECTION_TITLE[section],
          caption:
            opts.captions === false
              ? null
              : servedTogether && opts.serviceStyle === 'OVS'
                ? SERVED_TOGETHER_CAPTION
                : sectionCaption(section, opts.serviceStyle),
          rows: sectionRows,
        },
      ]
    })
    return {
      band,
      mealPattern,
      heading: gradeGroupHeading(band, mealPattern),
      sections,
      claimed: mains.length === 0 ? null : claimedMeals(claimRows),
    }
  })
}

type TransportCount = 'ordered' | 'prepared' | 'delivered'
type ServiceCount = 'served' | 'reimbursable' | 'nonReimbursable' | 'leftover'

/**
 * The item's transport figures that NO band row carries, per column, as one
 * row labelled with every band joined; null when each figure is already on
 * a band row (or the item has none).
 */
function itemTransportRow(row: MatrixItemRow): PrintBandRow | null {
  const onItemOnly = (count: TransportCount): number | null => {
    const value = row[count]
    return value !== null && row.tiers.every((tier) => tier[count] === null) ? value : null
  }
  const ordered = onItemOnly('ordered')
  const prepared = onItemOnly('prepared')
  const delivered = onItemOnly('delivered')
  if (ordered === null && prepared === null && delivered === null) {
    return null
  }
  const band = row.tiers.map((tier) => tier.label).join(', ') || ALL_GRADES_LABEL
  return {
    key: `${row.key}::${band}`,
    row,
    band,
    servingLabel: row.servingSize,
    firstOfItem: true,
    ordered,
    prepared,
    delivered,
    served: null,
    reimbursable: null,
    nonReimbursable: null,
    leftover: null,
  }
}

/**
 * The service counts left once every band row is summed (a roster bucket
 * with no grade range beside banded ones), as an `ALL_GRADES_LABEL` row;
 * null when nothing remains. A column the item does not carry stays null.
 */
function unbandedRemainderRow(row: MatrixItemRow): PrintBandRow | null {
  const remainderOf = (count: ServiceCount): number | null => {
    const total = row[count]
    if (total === null) {
      return null
    }
    return total - row.tiers.reduce((sum, tier) => sum + (tier[count] ?? 0), 0)
  }
  const served = remainderOf('served')
  const reimbursable = remainderOf('reimbursable')
  const nonReimbursable = remainderOf('nonReimbursable')
  const leftover = remainderOf('leftover')
  if (![served, reimbursable, nonReimbursable, leftover].some((value) => value !== null && value !== 0)) {
    return null
  }
  return {
    key: `${row.key}::${ALL_GRADES_LABEL}`,
    row,
    band: ALL_GRADES_LABEL,
    servingLabel: row.servingSize,
    firstOfItem: false,
    ordered: null,
    prepared: null,
    delivered: null,
    served,
    reimbursable,
    nonReimbursable,
    leftover,
  }
}

/**
 * The labelled legend of one printed band row: `legendFields` with the
 * ROW's serving label (per band) and the recipe codes. The band is NOT a
 * token any more: the grade-group block header names it (PROD-1441).
 */
export function printRowLegendFields(printRow: PrintBandRow, opts: { multiDelivery: boolean }): LegendField[] {
  return legendFields(printRow.row, {
    multiDelivery: opts.multiDelivery,
    includeRecipe: true,
    servingSize: printRow.servingLabel,
  })
}

/** `printRowLegendFields` as plain tokens, for the CSV export. */
export function printRowLegend(printRow: PrintBandRow, opts: { multiDelivery: boolean }): string[] {
  return printRowLegendFields(printRow, opts).map(legendFieldText)
}

export function buildUnifiedItemRows(doc: ProductionRecordDocument): UnifiedMatrix {
  const rows = new Map<string, RowAcc>()

  const ensureRow = (key: string, node: DocumentLineNode): RowAcc => {
    let acc = rows.get(key)
    if (!acc) {
      acc = {
        key,
        name: node.name,
        kindLabel: node.kindLabel,
        recipeIds: [],
        crediting: null,
        creditingMissing: false,
        servingSize: null,
        gradeSpans: new Set(),
        rateBps: null,
        isBulk: false,
        serviceStyle: null,
        deliveryIndexes: new Set(),
        ordered: null,
        prepared: null,
        delivered: null,
        served: null,
        reimbursable: null,
        nonReimbursable: null,
        leftover: null,
        enteredOrdered: null,
        enteredPrepared: null,
        enteredDelivered: null,
        temps: new Map(),
        components: new Map(),
        tiers: new Map(),
        isKit: false,
        recipeCodes: [],
        componentKind: null,
        componentKinds: new Set(),
        creditingSource: null,
        menuGradeSpan: null,
        mealKeys: new Set(),
        isMilk: false,
        fieldTripMeal: false,
        fieldTripSent: 0,
        fieldTripPlanned: 0,
      }
      rows.set(key, acc)
    }
    return acc
  }

  // Menu facts fold in from whichever node carries them first (transport
  // component leaves and service rows describe the same physical item).
  const foldFacts = (acc: RowAcc, node: DocumentLineNode) => {
    if ((acc.name === UNNAMED || acc.name === '') && node.name && node.name !== UNNAMED) {
      acc.name = node.name
    }
    acc.kindLabel ??= node.kindLabel
    addRecipeId(acc.recipeIds, node.recipeId)
    addRecipeId(acc.recipeCodes, node.recipeCode ?? null)
    acc.crediting ??= creditingSummary(node)
    acc.creditingMissing ||= node.creditingMissing
    acc.servingSize ??= node.recipeServingSize ?? node.servingLabel
    acc.componentKind ??= node.componentKind ?? null
    acc.creditingSource ??= node.creditingSource ?? null
    acc.menuGradeSpan ??= node.menuGradeSpan ?? null
    if (node.bundleId) {
      acc.mealKeys.add(`bundle:${node.bundleId}`)
    }
    if (node.crediting?.some((entry) => entry.groupKind === 'MILK')) {
      acc.isMilk = true
    }
    if (node.isKit) {
      acc.isKit = true
    }
    const span = tierLabelOf(node)
    if (span) {
      acc.gradeSpans.add(span)
    }
    acc.rateBps ??= node.deliveryRateBps
    acc.isBulk ||= node.isBulk
    acc.serviceStyle ??= node.serviceStyle
  }

  const tierFor = (acc: RowAcc, label: string, servingLabel: string | null, gradeRangeName: string | null): TierAcc => {
    let tier = acc.tiers.get(label)
    if (!tier) {
      tier = {
        label,
        gradeRangeName,
        servingLabel,
        ordered: null,
        prepared: null,
        delivered: null,
        served: null,
        reimbursable: null,
        nonReimbursable: null,
        leftover: null,
        enteredOrdered: null,
        enteredPrepared: null,
        enteredDelivered: null,
      }
      acc.tiers.set(label, tier)
    }
    tier.gradeRangeName ??= gradeRangeName
    tier.servingLabel ??= servingLabel
    return tier
  }

  // ---- Transport walk -----------------------------------------------------

  const keyForDeliverable = (node: DocumentLineNode, foodChildren: DocumentLineNode[]): string => {
    if (node.bundleDeliverableId) {
      return `del:${node.bundleDeliverableId}`
    }
    // Synthetic deliverables carry no id — key by the lone component, else name.
    const only = foodChildren.length === 1 ? foodChildren[0] : undefined
    return only?.componentId ? `cmp:${only.componentId}` : `name:${node.name}`
  }

  const visitDeliverable = (node: DocumentLineNode, groupIndex: number, bundleOrdered: number | null) => {
    if (node.isPackaging) {
      return
    }
    const foodChildren = node.children.filter(isFoodComponent)
    const acc = ensureRow(keyForDeliverable(node, foodChildren), node)
    acc.deliveryIndexes.add(groupIndex)
    // Ordered distributes from the bundle UNCHANGED (100 meals ⇒ 100 of each
    // package). While a bundle parent provides the real figure, the
    // deliverable's own counts.ordered is a prepared-echo — never read it,
    // and never sum: fan-out duplicates repeat the same total. On the FLAT
    // wire (children always [], bundle parents promoted away — product
    // direction 2026-08-06) the line's own ordered IS the presented figure,
    // and per-line values sum across homeroom fan-out like prepared.
    // A sealed kit (PROD-1441) is a flat line with sub-rows: its own ordered
    // is the presented figure, exactly like a childless flat line.
    const flatLine = node.children.length === 0 || node.isKit === true
    if (bundleOrdered !== null && acc.ordered === null) {
      acc.ordered = bundleOrdered
    } else if (bundleOrdered === null && flatLine) {
      acc.ordered = addNullable(acc.ordered, node.counts.ordered)
    }
    // Per-count coalesce (deliverable value, else its lone food child's),
    // then SUM across fan-out siblings.
    const lone = foodChildren.length === 1 ? foodChildren[0] : undefined
    const prepared = node.counts.prepared ?? lone?.counts.prepared ?? null
    const delivered = node.counts.delivered ?? lone?.counts.delivered ?? null
    acc.prepared = addNullable(acc.prepared, prepared)
    acc.delivered = addNullable(acc.delivered, delivered)
    // Only a band the line was actually shipped to mints a tier; a MENU span
    // (whole-campus line) reaches the legend through foldFacts below.
    const tierLabel = shippedBandOf(node) ?? (lone ? shippedBandOf(lone) : null)
    if (tierLabel) {
      const tier = tierFor(
        acc,
        tierLabel,
        node.servingLabel ?? lone?.servingLabel ?? null,
        node.gradeRangeName ?? lone?.gradeRangeName ?? null,
      )
      tier.prepared = addNullable(tier.prepared, prepared)
      tier.delivered = addNullable(tier.delivered, delivered)
      // The flat wire's per-line ordered is the band's own figure (same
      // guard as acc.ordered above); a bundle-distributed total is not
      // splittable per band and stays on the item.
      if (bundleOrdered === null && flatLine) {
        tier.ordered = addNullable(tier.ordered, node.counts.ordered)
      }
    }
    foldFacts(acc, node)
    if (lone) {
      foldFacts(acc, lone)
    } else if (foodChildren.length > 0) {
      foldComponents(acc, foodChildren, true)
    }
  }

  const visitStandaloneComponent = (node: DocumentLineNode, groupIndex: number, topLevel: boolean) => {
    if (node.isPackaging || !node.componentId) {
      return
    }
    const acc = ensureRow(componentRowKey(node), node)
    acc.deliveryIndexes.add(groupIndex)
    // Only TOP-LEVEL standalone components carry a genuine ordered count;
    // nested ones echo plannedQuantity. Every top-level line SUMS like
    // prepared: a true à-la-carte line carries its SHARE of the day's order
    // (`shareOrderedAcrossLines`, PROD-1610: the shares add up to the order
    // exactly once), and a PROMOTED component row (flat wire: kind COMPONENT
    // still carrying its deliverable's identity) its per-line figure. Keeping
    // the first line's figure printed one menu's order for milk sold from two
    // (Amana 2026-11-02 lunch: 11 of 84 chocolate, 72 of 83 whole).
    if (topLevel) {
      acc.ordered = addNullable(acc.ordered, node.counts.ordered)
    }
    acc.prepared = addNullable(acc.prepared, node.counts.prepared)
    acc.delivered = addNullable(acc.delivered, node.counts.delivered)
    const tierLabel = shippedBandOf(node)
    if (tierLabel) {
      const tier = tierFor(acc, tierLabel, node.servingLabel, node.gradeRangeName)
      tier.prepared = addNullable(tier.prepared, node.counts.prepared)
      tier.delivered = addNullable(tier.delivered, node.counts.delivered)
      // A promoted row's per-line ordered is the band's own (it sums like
      // prepared); an à-la-carte campus total repeats on every band line
      // and is not attributable to one, so it stays on the item.
      if (topLevel && node.bundleDeliverableId != null) {
        tier.ordered = addNullable(tier.ordered, node.counts.ordered)
      }
    }
    foldFacts(acc, node)
  }

  doc.transport.groups.forEach((group, gi) => {
    const groupIndex = gi + 1
    for (const top of group.lines) {
      if (top.isPackaging) {
        continue
      }
      if (top.kind === 'BUNDLE') {
        // Bundles are context, not rows. counts.ordered is genuine only when
        // the node has real bundle identity; synthetic parents echo the plan.
        const bundleOrdered = top.bundleId !== null ? top.counts.ordered : null
        for (const child of top.children) {
          if (child.kind === 'COMPONENT') {
            visitStandaloneComponent(child, groupIndex, false)
          } else {
            visitDeliverable(child, groupIndex, bundleOrdered)
          }
        }
      } else if (top.kind === 'DELIVERABLE') {
        visitDeliverable(top, groupIndex, null)
      } else {
        visitStandaloneComponent(top, groupIndex, true)
      }
    }
  })

  // ---- Service walk -------------------------------------------------------

  const serviceKeyOf = (node: DocumentLineNode): string =>
    node.kind === 'COMPONENT' && node.componentId
      ? componentRowKey(node)
      : node.bundleDeliverableId
        ? `del:${node.bundleDeliverableId}`
        : node.componentId
          ? `cmp:${node.componentId}`
          : node.menuItemId
            ? `mi:${node.menuItemId}`
            : `name:${node.name}`
  // A Field Trip menu milk line prints as its row's field-trip line only
  // beside the cafeteria's own line of that milk; alone, it IS the row.
  const cafeteriaKeys = new Set(
    doc.service.groups.flatMap((group) =>
      group.lines.filter((node) => node.isFieldTrip !== true).map((node) => serviceKeyOf(node)),
    ),
  )

  for (const group of doc.service.groups) {
    for (const node of group.lines) {
      // Kind-aware identity, mirroring the transport walk: a promoted
      // COMPONENT row keys by (deliverable, component) — NEVER by bare
      // `del:`, which is shared with its promoted siblings (that would merge
      // two food items into one row and double-count the service buckets
      // each inherited from the same deliverable) — and never by bare
      // `cmp:` either, which would merge the same component produced in TWO
      // different deliverables (see `componentRowKey`).
      const key =
        node.kind === 'COMPONENT' && node.componentId
          ? componentRowKey(node)
          : node.bundleDeliverableId
            ? `del:${node.bundleDeliverableId}`
            : node.componentId
              ? `cmp:${node.componentId}`
              : node.menuItemId
                ? `mi:${node.menuItemId}`
                : `name:${node.name}`
      const acc = ensureRow(key, node)
      acc.fieldTripMeal ||= node.fieldTripMeal === true
      acc.fieldTripSent += node.counts.fieldTrip ?? 0
      if (node.isFieldTrip === true && cafeteriaKeys.has(key)) {
        // The Field Trip menu's milk is the row's field-trip line: its order
        // prints there, never in Planned, and it carries no service of its own
        // beyond the cartons sent (Amana 2026-11-02: 73 lunch + 11 field trip).
        acc.fieldTripPlanned += node.counts.ordered ?? 0
        foldFacts(acc, node)
        continue
      }
      acc.served = addNullable(acc.served, node.counts.served)
      acc.reimbursable = addNullable(acc.reimbursable, node.counts.reimbursable)
      acc.nonReimbursable = addNullable(acc.nonReimbursable, node.counts.nonReimbursable)
      acc.leftover = addNullable(acc.leftover, node.counts.leftover)
      if (acc.ordered === null && node.counts.ordered !== null) {
        acc.ordered = node.counts.ordered
      }
      // PROD-1279: a figure the SCHOOL entered on its record (marked by
      // `counts.entered`) is summed apart and wins at finalize; an unmarked
      // one is the legacy proxy and never overrides the transport walk.
      const entered = node.counts.entered
      acc.enteredOrdered = addNullable(acc.enteredOrdered, entered?.ordered ? node.counts.ordered : null)
      acc.enteredPrepared = addNullable(acc.enteredPrepared, entered?.prepared ? node.counts.prepared : null)
      acc.enteredDelivered = addNullable(acc.enteredDelivered, entered?.delivered ? node.counts.delivered : null)
      foldFacts(acc, node)
      for (const portion of node.portions) {
        const label = portion.gradeSpan ?? portion.gradeRangeName
        if (!label) {
          continue
        }
        const tier = tierFor(acc, label, null, portion.gradeRangeName)
        // Per-tier ordered / prepared / delivered carry ENTERED values only
        // (the server emits null otherwise), so they fold as entered.
        tier.enteredOrdered = addNullable(tier.enteredOrdered, portion.ordered ?? null)
        tier.enteredPrepared = addNullable(tier.enteredPrepared, portion.prepared ?? null)
        tier.enteredDelivered = addNullable(tier.enteredDelivered, portion.delivered ?? null)
        tier.served = addNullable(tier.served, portion.served)
        tier.reimbursable = addNullable(tier.reimbursable, portion.reimbursable)
        tier.nonReimbursable = addNullable(tier.nonReimbursable, portion.nonReimbursable)
        tier.leftover = addNullable(tier.leftover, portion.leftover)
      }
      const foodChildren = node.children.filter(isFoodComponent)
      if (foodChildren.length === 1) {
        foldFacts(acc, foodChildren[0]!)
      } else if (foodChildren.length > 0) {
        foldComponents(acc, foodChildren, false)
      }
    }
  }

  // ---- Temperature attachment ----------------------------------------------

  const unmatchedByKey = new Map<string, MatrixUnmatchedTemp>()
  // A deliverable-level reading whose deliverable rendered as promoted
  // component row(s) has no bare `del:` row — attach it only when exactly one
  // row carries that deliverable (single-pick); ambiguous fan attaches nowhere
  // and falls through to the remaining fallbacks.
  const soleRowForDeliverable = (bundleDeliverableId: string): RowAcc | undefined => {
    const prefix = `del:${bundleDeliverableId}:`
    const matches = [...rows.values()].filter((row) => row.key.startsWith(prefix))
    return matches.length === 1 ? matches[0] : undefined
  }
  const attach = (reading: DocumentTempReading) => {
    const target =
      (reading.bundleDeliverableId && reading.componentId
        ? rows.get(`del:${reading.bundleDeliverableId}:cmp:${reading.componentId}`)
        : undefined) ??
      (reading.bundleDeliverableId ? rows.get(`del:${reading.bundleDeliverableId}`) : undefined) ??
      (reading.bundleDeliverableId ? soleRowForDeliverable(reading.bundleDeliverableId) : undefined) ??
      (reading.componentId ? rows.get(`cmp:${reading.componentId}`) : undefined) ??
      (reading.menuItemId ? rows.get(`mi:${reading.menuItemId}`) : undefined) ??
      (reading.lineName ? [...rows.values()].find((r) => r.name === reading.lineName) : undefined)
    const compact: MatrixTempReading = {
      value: reading.value,
      unit: reading.unit,
      status: reading.status,
      band: reading.band,
      loggedAt: reading.loggedAt,
    }
    const dedupKey = `${reading.checkpoint}::${reading.value}::${reading.unit}::${reading.status}`
    if (!target) {
      const key = `${dedupKey}::${reading.lineName ?? ''}`
      if (!unmatchedByKey.has(key)) {
        unmatchedByKey.set(key, { ...compact, checkpoint: reading.checkpoint, lineName: reading.lineName })
      }
      return
    }
    let cell = target.temps.get(reading.checkpoint)
    if (!cell) {
      cell = new Map()
      target.temps.set(reading.checkpoint, cell)
    }
    const existing = cell.get(dedupKey)
    // Fan-out duplicates collapse; keep the earliest timestamp for ordering.
    if (
      !existing ||
      (compact.loggedAt !== null && (existing.loggedAt === null || compact.loggedAt < existing.loggedAt))
    ) {
      cell.set(dedupKey, compact)
    }
  }
  for (const group of doc.transport.groups) {
    group.temps.forEach(attach)
  }
  for (const group of doc.service.groups) {
    group.temps.forEach(attach)
  }

  // ---- Merge same-deliverable-component rows across parent bundles ----------
  //
  // One physical component shipped inside several bundles (Baby Carrot packed
  // in three different calzone meals) is ONE deliverable to the school —
  // owner direction 2026-08-24: present one row per deliverable, quantities
  // summed across the parent-bundle segments, the bundle-suffixed names
  // collapsed to the shared component name. Segments merge only when their
  // production facts agree (crediting, serving size, bulk flag); segments
  // that genuinely differ — a bulk tray AND individually packed cups of the
  // same component — stay separate rows so the row's facts never lie.
  // Runs AFTER temp attachment so deliverable-scoped readings still find
  // their original per-deliverable rows.
  mergeDeliverableComponentRows(rows)

  // ---- Finalize --------------------------------------------------------------

  const byTime = (a: MatrixTempReading, b: MatrixTempReading): number => {
    if (a.loggedAt === b.loggedAt) {
      return 0
    }
    if (a.loggedAt === null) {
      return 1
    }
    if (b.loggedAt === null) {
      return -1
    }
    return a.loggedAt < b.loggedAt ? -1 : 1
  }

  const health: TempHealth = { total: 0, inRange: 0, outOfRange: 0, notRecorded: 0 }
  const count = (reading: MatrixTempReading) => {
    health.total += 1
    if (reading.status === 'WITHIN_RANGE') {
      health.inRange += 1
    } else if (reading.status === 'OUT_OF_RANGE') {
      health.outOfRange += 1
    } else {
      health.notRecorded += 1
    }
  }

  const presentCheckpoints = new Set<MatrixCheckpoint>()
  const finalRows: MatrixItemRow[] = [...rows.values()].map((acc) => {
    const temps: MatrixItemRow['temps'] = {}
    for (const [checkpoint, cell] of acc.temps) {
      // Re-checks read oldest → newest ("47.5 → 39.0"); the wire is DESC.
      const readings = [...cell.values()].sort(byTime)
      readings.forEach(count)
      presentCheckpoints.add(checkpoint)
      temps[checkpoint] = { readings, outOfRange: readings.some((r) => r.status === 'OUT_OF_RANGE') }
    }
    const tiers = acc.tiers.size > 1 ? [...acc.tiers.values()].map(finalizeTier) : []
    return {
      key: acc.key,
      name: acc.name,
      kindLabel: acc.kindLabel,
      recipeIds: [...acc.recipeIds],
      crediting: acc.crediting,
      creditingMissing: acc.creditingMissing,
      servingSize: acc.servingSize,
      // Bands rendered as tiers carry the text themselves. Otherwise exactly
      // one banded tier is the band actually shipped / served and wins over
      // any menu-level span the service seed added (a K-5 shipment line
      // beside a K-8 menu fallback used to collapse to NOTHING); else the
      // single span; else the first-seen span (transport walk first).
      gradeSpan:
        tiers.length > 0
          ? null
          : acc.tiers.size === 1
            ? [...acc.tiers.keys()][0]!
            : acc.gradeSpans.size > 0
              ? [...acc.gradeSpans][0]!
              : null,
      rateBps: acc.rateBps,
      isBulk: acc.isBulk,
      serviceStyle: acc.serviceStyle,
      deliveryIndexes: [...acc.deliveryIndexes].sort((a, b) => a - b),
      // The record wins (PROD-1279): what the school entered on its own
      // record beats the shipment-derived default; nothing entered, nothing changes.
      ordered: acc.enteredOrdered ?? acc.ordered,
      prepared: acc.enteredPrepared ?? acc.prepared,
      delivered: acc.enteredDelivered ?? acc.delivered,
      served: acc.served,
      reimbursable: acc.reimbursable,
      nonReimbursable: acc.nonReimbursable,
      leftover: acc.leftover,
      temps,
      components: acc.components.size >= 2 ? [...acc.components.values()] : [],
      tiers,
      isKit: acc.isKit && acc.components.size >= 2,
      recipeCodes: [...acc.recipeCodes],
      componentKind: acc.componentKind,
      section: sectionOf(acc),
      sortRank: rowKindRank(rowKinds(acc)),
      creditingSource: acc.creditingSource,
      menuGradeSpan: acc.menuGradeSpan,
      mealKeys: [...acc.mealKeys],
      receivedIsStartedWith: acc.isMilk && acc.enteredDelivered !== null,
      isMilk: acc.isMilk,
      fieldTrip:
        acc.fieldTripSent > 0 || acc.fieldTripPlanned > 0
          ? { sent: acc.fieldTripSent, planned: acc.fieldTripPlanned }
          : null,
      ...(acc.fieldTripMeal ? { fieldTripMeal: true } : {}),
    }
  })

  const unmatched = [...unmatchedByKey.values()].sort(
    (a, b) => CHECKPOINT_ORDER.indexOf(a.checkpoint) - CHECKPOINT_ORDER.indexOf(b.checkpoint),
  )
  for (const reading of unmatched) {
    count(reading)
    presentCheckpoints.add(reading.checkpoint)
  }

  const deliveries: MatrixDelivery[] = doc.transport.groups.map((group, gi) => ({
    index: gi + 1,
    deliveryMode: group.deliveryMode,
    handoutMode: group.handoutMode,
    deliveryDate: group.deliveryDate,
    dayBefore: group.deliveryDateDiffersFromService,
    receivedAt: group.receivedAt,
  }))
  const pairs = new Set(deliveries.map((d) => `${d.deliveryMode}::${d.handoutMode}`))
  const sharedModePair =
    deliveries.length > 0 && pairs.size === 1
      ? { deliveryMode: deliveries[0]!.deliveryMode, handoutMode: deliveries[0]!.handoutMode }
      : null

  return {
    rows: relocateFieldTripMilk(finalRows),
    checkpoints: CHECKPOINT_ORDER.filter((cp) => presentCheckpoints.has(cp)),
    unmatched,
    tempHealth: health,
    deliveries,
    sharedModePair,
  }
}

// ---------------------------------------------------------------------------
// Compliance chips (the whole status story — no tiles, no timeline)
// ---------------------------------------------------------------------------

export interface ComplianceChip {
  key: 'kitchen' | 'delivery' | 'service' | 'temps'
  label: string
  value: string
  tone: 'ok' | 'warn' | 'bad' | 'muted'
  /** Tooltip: capture context + timestamp. */
  title: string | null
  /** Jump anchor (the temps chip links to the first flagged reading). */
  href: string | null
}

/** Anchor id the temps chip jumps to (the first out-of-range cell sets it). */
export const OUT_OF_RANGE_ANCHOR = 'temp-out-of-range'

function signatureChip(
  key: ComplianceChip['key'],
  label: string,
  rollup: SignatureRollup,
  capturedVia: string,
  formatTime: (iso: string) => string,
): ComplianceChip | null {
  if (rollup.state === 'none') {
    return null
  }
  const title = rollup.signedAt ? `${capturedVia} · ${formatTime(rollup.signedAt)}` : capturedVia
  if (rollup.state === 'signed') {
    return { key, label, value: rollup.name ?? 'Signed', tone: 'ok', title, href: null }
  }
  if (rollup.state === 'partial') {
    return { key, label, value: `${rollup.signedCount}/${rollup.total} signed`, tone: 'warn', title, href: null }
  }
  return { key, label, value: 'Unsigned', tone: 'warn', title: capturedVia, href: null }
}

export function complianceChips(
  doc: ProductionRecordDocument,
  tempHealth: TempHealth,
  formatTime: (iso: string) => string,
): ComplianceChip[] {
  const chips: Array<ComplianceChip | null> = [
    signatureChip('kitchen', 'Kitchen', kitchenSignatureRollup(doc), 'At facility temp check', formatTime),
    signatureChip('delivery', 'Delivery', deliverySignatureRollup(doc), 'At delivery receipt', formatTime),
    signatureChip('service', 'Service', serviceSignatureRollup(doc), 'Signed at service close', formatTime),
  ]
  if (tempHealth.total === 0) {
    chips.push({ key: 'temps', label: 'Temps', value: 'No readings', tone: 'muted', title: null, href: null })
  } else if (tempHealth.outOfRange > 0) {
    chips.push({
      key: 'temps',
      label: 'Temps',
      value: `${tempHealth.outOfRange} out of range`,
      tone: 'bad',
      title: `${tempHealth.inRange}/${tempHealth.total} within range`,
      href: `#${OUT_OF_RANGE_ANCHOR}`,
    })
  } else {
    chips.push({
      key: 'temps',
      label: 'Temps',
      value: `${tempHealth.inRange}/${tempHealth.total}`,
      tone: 'ok',
      title: 'Every recorded reading within range',
      href: null,
    })
  }
  return chips.filter((c): c is ComplianceChip => c !== null)
}

// ---------------------------------------------------------------------------
// Small display derivations
// ---------------------------------------------------------------------------

/** Prepared-vs-received shortfall ("198 ▼2"); null when nothing is short. */
export function receivedShortfall(prepared: number | null, delivered: number | null): number | null {
  if (prepared === null || delivered === null) {
    return null
  }
  const short = prepared - delivered
  return short > 0 ? short : null
}

export interface ServiceStyleDisplay {
  label: string
  /** Where the style came from — tooltip copy, never printed. */
  provenance: string | null
}

export function serviceStyleDisplay(doc: ProductionRecordDocument): ServiceStyleDisplay {
  if (doc.serviceStyle === null) {
    return { label: 'Mixed service styles', provenance: 'per-line styles shown in the table' }
  }
  const label = doc.serviceStyle === 'OVS' ? 'Offer vs. Serve (OVS)' : 'Straight Serve (SS)'
  const provenance =
    doc.serviceStyleSource === 'MENU'
      ? 'from menu settings'
      : doc.serviceStyleSource === 'LOCATION'
        ? 'campus override'
        : doc.serviceStyleSource === 'ORGANIZATION'
          ? 'organization default'
          : null
  return { label, provenance }
}

/**
 * Delivery header chip: a single delivery shows its actual date
 * ("Delivered Wed, Aug 5, 2026 (day-before)"); multiple deliveries show
 * the count (their dates live on the per-delivery meta lines). Null when
 * no shipments exist.
 */
export function deliverySummary(doc: ProductionRecordDocument): string | null {
  const groups = doc.transport.groups
  if (groups.length === 0) {
    return null
  }
  if (groups.length === 1) {
    const group = groups[0]!
    return `Delivered ${formatServiceDate(group.deliveryDate)}${group.deliveryDateDiffersFromService ? ' (day-before)' : ''}`
  }
  const dayBefore = groups.filter((g) => g.deliveryDateDiffersFromService).length
  const base = `${groups.length} deliveries`
  if (dayBefore === 0) {
    return base
  }
  return `${base} · ${dayBefore === groups.length ? 'all' : dayBefore} day-before`
}
